import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Droplets,
  Sun,
  Thermometer,
  Leaf,
  AlertTriangle,
  Wrench,
  HelpCircle,
  Check,
} from "lucide-react";

interface CareInstructionsProps {
  terrariumStyle: string;
  hasActiveSubscription: boolean;
  token: string;
}

interface CareGuide {
  watering: {
    frequency: string;
    amount: string;
    tips: string[];
  };
  light: {
    type: string;
    duration: string;
    tips: string[];
  };
  temperature: {
    range: string;
    tips: string[];
  };
  troubleshooting: {
    problem: string;
    solution: string;
  }[];
}

const careGuides: Record<string, CareGuide> = {
  forest: {
    watering: {
      frequency: "Every 2-3 weeks",
      amount: "1-2 tablespoons",
      tips: [
        "Check the soil moisture before watering - it should feel slightly dry on top",
        "Avoid overwatering - mosses and ferns prefer humidity, not soggy soil",
        "Mist the glass walls occasionally to maintain humidity",
        "If you see condensation on the glass, skip watering",
      ],
    },
    light: {
      type: "Bright indirect light",
      duration: "6-8 hours daily",
      tips: [
        "Place near a north or east-facing window",
        "Avoid direct sunlight which can scorch delicate moss",
        "Rotate the terrarium occasionally for even growth",
        "Fluorescent or LED grow lights work well if natural light is limited",
      ],
    },
    temperature: {
      range: "60-75 F (15-24 C)",
      tips: [
        "Keep away from heating vents and air conditioners",
        "Avoid placing near cold windows in winter",
        "Consistent temperature is more important than exact degrees",
      ],
    },
    troubleshooting: [
      {
        problem: "Yellow or brown moss",
        solution: "Usually a sign of too much direct light or underwatering. Move to a shadier spot and increase misting.",
      },
      {
        problem: "Mold or fungus growth",
        solution: "Too much moisture. Remove the lid for a few hours daily to improve air circulation and reduce watering.",
      },
      {
        problem: "Condensation buildup",
        solution: "This is normal! If excessive, leave the lid slightly open for a few hours.",
      },
      {
        problem: "Leggy or stretched plants",
        solution: "Not enough light. Move to a brighter location (still avoiding direct sun).",
      },
    ],
  },
  desert: {
    watering: {
      frequency: "Every 3-4 weeks",
      amount: "1 tablespoon near roots",
      tips: [
        "Less is more - succulents and cacti store water in their leaves",
        "Water only when soil is completely dry",
        "Avoid getting water on the leaves to prevent rot",
        "In winter, reduce watering to once every 4-6 weeks",
      ],
    },
    light: {
      type: "Bright direct to indirect light",
      duration: "8-10 hours daily",
      tips: [
        "Place near a south or west-facing window",
        "Succulents love sunlight - more is generally better",
        "Rotate regularly for even, compact growth",
        "Some direct sunlight is beneficial for desert plants",
      ],
    },
    temperature: {
      range: "65-80 F (18-27 C)",
      tips: [
        "Desert plants tolerate temperature fluctuations well",
        "Cooler nights are actually beneficial",
        "Keep away from frost and freezing temperatures",
      ],
    },
    troubleshooting: [
      {
        problem: "Soft, mushy leaves",
        solution: "Overwatering! Allow soil to dry completely before watering again. Remove any rotting leaves.",
      },
      {
        problem: "Stretched or elongated growth",
        solution: "Not enough light. Move to a sunnier location.",
      },
      {
        problem: "Brown, dry leaf tips",
        solution: "Underwatering or too much direct sun. Adjust accordingly.",
      },
      {
        problem: "Pests (mealybugs, scale)",
        solution: "Isolate the terrarium and treat with rubbing alcohol on a cotton swab.",
      },
    ],
  },
  tropical: {
    watering: {
      frequency: "Every 1-2 weeks",
      amount: "2-3 tablespoons",
      tips: [
        "Tropical plants love humidity - regular misting is appreciated",
        "Keep soil consistently moist but not waterlogged",
        "Use room temperature water to avoid shocking the plants",
        "If leaves droop, it's usually time to water",
      ],
    },
    light: {
      type: "Bright indirect light",
      duration: "8-10 hours daily",
      tips: [
        "Avoid direct harsh sunlight which can burn leaves",
        "East-facing windows are ideal",
        "Supplement with grow lights in darker months",
        "Vibrant leaf colors indicate good light levels",
      ],
    },
    temperature: {
      range: "65-80 F (18-27 C)",
      tips: [
        "Tropical plants thrive in warm, humid conditions",
        "Avoid cold drafts and sudden temperature changes",
        "Bathroom or kitchen placement can provide natural humidity",
      ],
    },
    troubleshooting: [
      {
        problem: "Brown leaf edges",
        solution: "Low humidity. Increase misting or place near a humidity source.",
      },
      {
        problem: "Yellowing leaves",
        solution: "Could be overwatering or nutrient deficiency. Check soil drainage.",
      },
      {
        problem: "Drooping leaves",
        solution: "Usually needs water. Check soil moisture and water if dry.",
      },
      {
        problem: "Slow growth",
        solution: "Likely needs more light or warmer temperatures.",
      },
    ],
  },
  deluxe: {
    watering: {
      frequency: "Varies by plants (typically every 1-2 weeks)",
      amount: "Check individual plant needs",
      tips: [
        "Your deluxe terrarium has a custom plant selection",
        "Monitor each plant type's specific needs",
        "Keep a watering journal to track what works best",
        "When in doubt, underwater rather than overwater",
      ],
    },
    light: {
      type: "Bright indirect light (varies by plants)",
      duration: "6-10 hours daily",
      tips: [
        "Observe how plants respond to their current position",
        "Adjust placement based on plant health",
        "Most terrarium plants prefer filtered light",
        "Consider supplemental lighting if natural light is limited",
      ],
    },
    temperature: {
      range: "60-80 F (15-27 C)",
      tips: [
        "Maintain consistent room temperature",
        "Avoid extreme temperature fluctuations",
        "Keep away from heating/cooling vents",
      ],
    },
    troubleshooting: [
      {
        problem: "General plant decline",
        solution: "Check water, light, and temperature. Make one change at a time to identify the issue.",
      },
      {
        problem: "Different plants have different needs",
        solution: "Research each plant type in your terrarium for specific care guidance.",
      },
      {
        problem: "Overcrowding",
        solution: "Prune back plants that are growing too large to maintain balance.",
      },
      {
        problem: "Persistent issues",
        solution: "Contact us for a personalized care consultation or request a maintenance visit.",
      },
    ],
  },
};

export function CareInstructions({
  terrariumStyle,
  hasActiveSubscription,
  token,
}: CareInstructionsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceNotes, setMaintenanceNotes] = useState("");

  const guide = careGuides[terrariumStyle] || careGuides.deluxe;

  const maintenanceMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/portal/${token}/maintenance-request`, {
        notes: maintenanceNotes,
      });
    },
    onSuccess: () => {
      toast({
        title: "Maintenance Requested",
        description: "We'll be in touch soon to schedule your terrarium refresh!",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/portal/${token}`] });
      setMaintenanceDialogOpen(false);
      setMaintenanceNotes("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to request maintenance. Please try again.",
        variant: "destructive",
      });
    },
  });

  const styleNames: Record<string, string> = {
    forest: "Forest Oasis",
    desert: "DesertScape",
    tropical: "Tropical Paradise",
    deluxe: "Deluxe Custom",
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Leaf className="w-5 h-5 text-primary" />
              Care Instructions
            </CardTitle>
            <CardDescription>
              Personalized care guide for your {styleNames[terrariumStyle] || "terrarium"}
            </CardDescription>
          </div>
          {hasActiveSubscription && (
            <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Wrench className="w-4 h-4 mr-2" />
                  Need a Refresh?
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Maintenance Visit</DialogTitle>
                  <DialogDescription>
                    As an insurance subscriber, you're entitled to maintenance visits.
                    We'll come refresh your terrarium and ensure it's thriving!
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">
                      What would you like us to address? (Optional)
                    </label>
                    <Textarea
                      placeholder="e.g., Some plants look droopy, moss is browning, needs general cleanup..."
                      value={maintenanceNotes}
                      onChange={(e) => setMaintenanceNotes(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={() => maintenanceMutation.mutate()}
                    disabled={maintenanceMutation.isPending}
                  >
                    {maintenanceMutation.isPending ? "Submitting..." : "Request Maintenance"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Reference Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Droplets className="w-5 h-5 text-blue-500" />
              <h4 className="font-medium">Watering</h4>
            </div>
            <p className="text-sm font-medium">{guide.watering.frequency}</p>
            <p className="text-xs text-muted-foreground">{guide.watering.amount}</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-5 h-5 text-yellow-500" />
              <h4 className="font-medium">Light</h4>
            </div>
            <p className="text-sm font-medium">{guide.light.type}</p>
            <p className="text-xs text-muted-foreground">{guide.light.duration}</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-950/30 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Thermometer className="w-5 h-5 text-orange-500" />
              <h4 className="font-medium">Temperature</h4>
            </div>
            <p className="text-sm font-medium">{guide.temperature.range}</p>
          </div>
        </div>

        <Separator />

        {/* Detailed Care Instructions */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="watering">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Droplets className="w-4 h-4 text-blue-500" />
                Watering Schedule
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2">
                {guide.watering.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="light">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-yellow-500" />
                Light Requirements
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2">
                {guide.light.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="temperature">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-orange-500" />
                Temperature Tips
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2">
                {guide.temperature.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="troubleshooting">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Troubleshooting
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                {guide.troubleshooting.map((item, index) => (
                  <div key={index} className="bg-muted/30 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{item.problem}</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">{item.solution}</p>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {!hasActiveSubscription && (
          <>
            <Separator />
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Tip:</strong> Insurance subscribers get free maintenance visits and plant replacements.
                Contact us to add insurance to your order!
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
